import { z } from "zod/v4";
import { BooleanSchema } from "../ea.schema.helpers";
import { BaseFilterSchema, BaseWhereSchema, BoolFilterFieldSchema } from "../ea.schema.query.utils";
import { ExecutionJobLog, ExecutionLog, ExecutionStepLog, Scheduler } from "../ea.types";

// Loopback filter schema
const IntFilterSchema = z
  .object({
    like: z.coerce.number().optional(),
    ilike: z.coerce.number().optional(),
    inq: z.coerce.number().array().optional(),
    nin: z.coerce.number().array().optional(),
    lt: z.coerce.number().optional(),
    lte: z.coerce.number().optional(),
    gt: z.coerce.number().optional(),
    gte: z.coerce.number().optional(),
    not: z.union([z.coerce.number(), z.lazy(() => IntFilterSchema)]).optional(),
  })
  .strict();

// Loopback filter schema
const BoolFilterSchema = z
  .object({
    equals: BooleanSchema.optional(),
    not: z.union([BooleanSchema, z.lazy(() => BoolFilterSchema)]).optional(),
  })
  .strict();

// Loopback filter schema
const DateTimeFilterSchema = z
  .object({
    like: z.coerce.date().optional(),
    inq: z.coerce.date().array().optional(),
    nin: z.coerce.date().array().optional(),
    lt: z.coerce.date().optional(),
    lte: z.coerce.date().optional(),
    gt: z.coerce.date().optional(),
    gte: z.coerce.date().optional(),
    not: z.union([z.coerce.date(), z.lazy(() => DateTimeFilterSchema)]).optional(),
  })
  .strict();

// Loopback filter schema
const StringFilterSchema = z
  .object({
    like: z.string().optional(),
    ilike: z.string().optional(),
    inq: z.string().array().optional(),
    nin: z.string().array().optional(),
    lt: z.string().optional(),
    lte: z.string().optional(),
    gt: z.string().optional(),
    gte: z.string().optional(),
    not: z.union([z.string(), z.lazy(() => StringFilterSchema)]).optional(),
  })
  .strict();

// overrides for current schemas to be removed when we drop legacy loopback query schema
const StringFilterFieldSchema = z.union([z.lazy(() => StringFilterSchema), z.string()]);
const NumberFilterFieldSchema = z.union([z.lazy(() => IntFilterSchema), z.coerce.number()]);
const DataTimeFilterFieldSchema = z.union([z.lazy(() => DateTimeFilterSchema), z.coerce.date()]);

const createSelectSchema = <T extends Array<string>>(fields: T) => {
  return z
    .record(z.enum(fields), BooleanSchema.optional())
    .transform((value) => {
      const result = Object.fromEntries(
        Object.entries(value).filter(([, v]) => v === true),
      ) as Partial<Record<(typeof fields)[number], true>>;
      if (Object.keys(result).length === 0) {
        return undefined;
      }
      return result;
    })
    .optional();
};

// Loopback filter schema
const HistoryWhereSchema = BaseWhereSchema(() => HistoryWhereSchema)
  .extend({
    sessionId: StringFilterFieldSchema,
    state: StringFilterFieldSchema,
    status: StringFilterFieldSchema,
    scriptId: NumberFilterFieldSchema,
    scriptName: StringFilterFieldSchema,
    schedulerJobId: NumberFilterFieldSchema,
    schedulerJobName: StringFilterFieldSchema,
    projectId: NumberFilterFieldSchema,
    projectName: StringFilterFieldSchema,
    envName: StringFilterFieldSchema,
    virtualUserName: StringFilterFieldSchema,
    executorName: StringFilterFieldSchema,
    jobExecutionId: StringFilterFieldSchema,
    jobId: StringFilterFieldSchema,
    internal: BoolFilterFieldSchema,
    startTime: DataTimeFilterFieldSchema,
    endTime: DataTimeFilterFieldSchema,
  })
  .partial();

const JobHistoryWhereSchema = BaseWhereSchema(() => JobHistoryWhereSchema)
  .extend({
    jobId: StringFilterFieldSchema,
    state: StringFilterFieldSchema,
    status: StringFilterFieldSchema,
    isExcluded: BoolFilterFieldSchema,
    schedulerJobName: StringFilterFieldSchema,
    schedulerJobId: NumberFilterFieldSchema,
    startTime: DataTimeFilterFieldSchema,
    endTime: DataTimeFilterFieldSchema,
    projectId: NumberFilterFieldSchema,
    projectName: StringFilterFieldSchema,
    jobExecutionId: StringFilterFieldSchema,
  })
  .partial();

export const defaultExecutionLogFieldsName: Array<keyof ExecutionLog> = [
  "id",
  "sessionId",
  "scriptId",
  "projectId",
  "projectName",
  "url",
  "envName",
  "virtualUserName",
  "scriptName",
  "scriptVersion",
  "startTime",
  "endTime",
  "messages",
  "duration",
  "state",
  "scriptStatus",
  "status",
  "schedulerJobName",
  "schedulerJobId",
  "apiKeyName",
  "executorName",
  "integrationMetadata",
  "jobId",
  "internal",
];

export const HistoryFilterSchema = BaseFilterSchema.extend({
  where: HistoryWhereSchema.optional(),
  select: createSelectSchema(defaultExecutionLogFieldsName),
}).strict();

export const HistoryRequestSchema = z.object({
  filter: HistoryFilterSchema.optional(),
  withSteps: z.coerce.boolean().optional(),
});

const ScriptHistoryWhereSchema = BaseWhereSchema(() => ScriptHistoryWhereSchema)
  .extend({
    executionId: NumberFilterFieldSchema,
    schedulerJobId: NumberFilterFieldSchema,
    status: StringFilterFieldSchema,
    startTime: DataTimeFilterFieldSchema,
    endTime: DataTimeFilterFieldSchema,
    linkedScriptId: NumberFilterFieldSchema,
    workItemId: NumberFilterFieldSchema,
    duration: NumberFilterFieldSchema,
  })
  .partial();

export const defaultExecutionStepLogFieldsName: Array<keyof ExecutionStepLog> = [
  "id",
  "executionId",
  "lineNum",
  "labelKey",
  "labelParams",
  "label",
  "startTime",
  "endTime",
  "duration",
  "status",
];

export const ScriptHistoryFilterSchema = BaseFilterSchema.extend({
  where: ScriptHistoryWhereSchema.optional(),
  select: createSelectSchema(defaultExecutionStepLogFieldsName),
}).strict();

export const ScriptHistoryRequestSchema = z.object({
  filter: ScriptHistoryFilterSchema.optional(),
});

export const defaultSchedulerAggregatedHistoryFieldsName: Array<keyof ExecutionJobLog> = [
  "id",
  "jobId",
  "startTime",
  "endTime",
  "state",
  "status",
  "successCount",
  "warningCount",
  "errorCount",
  "allCount",
  "duration",
  "isExcluded",
  "schedulerJobId",
  "schedulerJobName",
  "projectId",
  "projectName",
];

export const SchedulerAggregatedHistoryFilterSchema = BaseFilterSchema.extend({
  where: JobHistoryWhereSchema.optional(),
  select: createSelectSchema(defaultSchedulerAggregatedHistoryFieldsName),
}).strict();

export const SchedulerAggregatedHistoryRequestSchema = z.object({
  filter: SchedulerAggregatedHistoryFilterSchema.optional(),
  select: z.looseObject({}).catchall(BoolFilterFieldSchema).optional(),
});

export const defaultSchedulerErrorsFieldsName: Array<keyof ExecutionLog> = [
  "id",
  "jobId",
  "sessionId",
  "scriptId",
  "scriptName",
  "virtualUserName",
  "envName",
  "endTime",
  "startTime",
  "url",
  "schedulerJobId",
  "schedulerJobName",
];

export const SchedulerErrorsFilterSchema = BaseFilterSchema.extend({
  where: HistoryWhereSchema.optional(),
  select: createSelectSchema(defaultSchedulerErrorsFieldsName),
}).strict();

export const SchedulerErrorsRequestSchema = z.object({
  filter: SchedulerErrorsFilterSchema.optional(),
});

const SchedulersWhereSchema = BaseWhereSchema(() => SchedulersWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    runMode: StringFilterFieldSchema,
    status: StringFilterFieldSchema,
    startDateTime: DataTimeFilterFieldSchema,
    endDateTime: DataTimeFilterFieldSchema,
    triggerType: StringFilterFieldSchema,
  })
  .partial();

export const defaultScherulerFieldsName: Array<keyof Scheduler> = [
  "id",
  "name",
  "description",
  "startDateTime",
  "endDateTime",
  "triggerType",
  "recurrenceType",
  "createdAt",
  "status",
  "mode",
  "runMode",
];

export const SchedulersFilterSchema = BaseFilterSchema.extend({
  where: SchedulersWhereSchema.optional(),
  select: createSelectSchema(defaultScherulerFieldsName),
}).strict();

export const SchedulersRequestSchema = z.object({
  filter: SchedulersFilterSchema.optional(),
});

const ScriptsWhereSchema = BaseWhereSchema(() => ScriptsWhereSchema)
  .extend({
    id: NumberFilterFieldSchema,
    name: StringFilterFieldSchema,
    status: StringFilterFieldSchema,
    projectId: NumberFilterFieldSchema,
    virtualUserId: NumberFilterFieldSchema,
  })
  .partial();

export const ScriptsFilterSchema = BaseFilterSchema.extend({
  where: ScriptsWhereSchema.optional(),
}).strict();

export const ScriptsRequestSchema = z.object({
  filter: ScriptsFilterSchema.optional(),
  withHistory: z.coerce.boolean().optional(),
  limitHistory: z.coerce.number().optional(),
});
